// Native media requests remain in their actual live customer window. Neither
// the service worker nor its opaque local URL carries an Auth/access credential.
const entries = new Map();
let ready, listening = false;
function bounded(operation) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('The protected media worker did not become ready. Reload this page and retry.')), 15000);
    operation.then(value => { clearTimeout(timer); resolve(value); }, error => { clearTimeout(timer); reject(error); });
  });
}
function span(range, total) {
  if (range === null) return { range: null, status: 200, start: 0, end: total - 1 };
  const match = typeof range === 'string' && /^bytes=(\d*)-(\d*)$/.exec(range);
  if (!match || !match[1] && !match[2]) throw new Error('A single media range is required.');
  const start = match[1] ? Number(match[1]) : Math.max(0, total - Number(match[2]));
  const end = match[1] && match[2] ? Math.min(total - 1, Number(match[2])) : total - 1;
  if (!Number.isSafeInteger(start) || !Number.isSafeInteger(end) || start < 0 || start > end || start >= total
    || !match[1] && (!Number.isSafeInteger(Number(match[2])) || Number(match[2]) < 1)) throw new Error('The media range is unavailable.');
  return { range: `bytes=${start}-${end}`, status: 206, start, end };
}
async function enable() {
  if (!window.isSecureContext || !navigator.serviceWorker) throw new Error('Protected media needs a secure connection and service worker support.');
  if (!listening) {
    listening = true;
    navigator.serviceWorker.addEventListener('message', async event => {
      if (event.source !== navigator.serviceWorker.controller || event.data?.kind !== 'CONTENT_MEDIA_READ_V3' || event.ports.length !== 1) return;
      const entry = entries.get(event.data.token), port = event.ports[0];
      if (!entry || !['GET', 'HEAD'].includes(event.data.method)) { port.postMessage({ kind: 'ERROR' }); port.close(); return; }
      const controller = new AbortController(); entry.requests.add(controller);
      let reader, ended = false, pulling = false;
      const finish = () => { if (ended) return; ended = true; controller.abort(); entry.requests.delete(controller); port.close(); void reader?.cancel().catch(() => {}); };
      const fail = () => { if (!ended) port.postMessage({ kind: 'ERROR' }); finish(); };
      try {
        entry.check();
        port.onmessage = async message => {
          if (message.data?.kind === 'CANCEL') return finish();
          if (message.data?.kind !== 'PULL' || pulling || !reader || ended) return fail();
          pulling = true;
          try {
            entry.check(); const chunk = await reader.read(); entry.check();
            if (ended) return;
            if (chunk.done) { port.postMessage({ kind: 'END' }); finish(); }
            else { const bytes = new Uint8Array(chunk.value).buffer; port.postMessage({ kind: 'CHUNK', bytes }, [bytes]); }
          } catch { fail(); } finally { pulling = false; }
        };
        port.onmessageerror = fail;
        const file = await entry.read(controller.signal); entry.check();
        if (ended || !Number.isSafeInteger(file.sizeBytes) || file.sizeBytes <= 0 || !/^(video|audio|image)\/[a-z0-9.+-]+$/.test(file.mimeType)) throw new Error('Invalid current media.');
        const selected = span(event.data.range, file.sizeBytes), bridge = new TransformStream();
        reader = bridge.readable.getReader();
        port.postMessage({ kind: 'HEADERS', status: selected.status, mimeType: file.mimeType,
          length: selected.end - selected.start + 1, contentRange: selected.status === 206 ? `bytes ${selected.start}-${selected.end}/${file.sizeBytes}` : null });
        // Only a single complete-object request may use the full transfer
        // receipt path. Native 206 headers are preserved for the player.
        const ownerRange = selected.start === 0 && selected.end === file.sizeBytes - 1 ? null : selected.range;
        if (event.data.method === 'GET') void file.stream(ownerRange, bridge.writable, controller.signal).catch(fail);
      } catch { fail(); }
    });
  }
  const script = new URL('/content-media-sw.js', location.origin).href, existing = await bounded(navigator.serviceWorker.getRegistration('/'));
  if (existing?.active && existing.active.scriptURL !== script) throw new Error('Another application controls this browser scope. Open the current Website in a new browser profile.');
  await bounded(navigator.serviceWorker.register(script, { scope: '/', updateViaCache: 'none' }));
  const controller = await new Promise((resolve, reject) => {
    let observed = null;
    const stop = () => { clearTimeout(timer); navigator.serviceWorker.removeEventListener('controllerchange', changed); observed?.removeEventListener('statechange', changed); };
    const changed = () => {
      const value = navigator.serviceWorker.controller;
      if (!value) return;
      if (value.scriptURL !== script || value.state === 'redundant') { stop(); reject(new Error('Another application controls this browser scope. Reload the current Website before opening media.')); return; }
      if (value.state === 'activated') { stop(); resolve(value); return; }
      if (observed !== value) { observed?.removeEventListener('statechange', changed); observed = value; value.addEventListener('statechange', changed); }
    };
    const timer = setTimeout(() => { stop(); reject(new Error('Reload this page to enable protected media.')); }, 15000);
    navigator.serviceWorker.addEventListener('controllerchange', changed); changed();
  });
  await new Promise((resolve, reject) => {
    const channel = new MessageChannel(), stop = () => { clearTimeout(timer); channel.port1.close(); };
    const timer = setTimeout(() => { stop(); reject(new Error('Reload this page after the media worker updates.')); }, 5000);
    channel.port1.onmessage = event => { if (event.data?.kind === 'CONTENT_MEDIA_READY_V3') { stop();
      if (navigator.serviceWorker.controller !== controller) reject(new Error('The media worker changed. Reopen this lesson.')); else resolve(); } };
    controller.postMessage({ kind: 'CONTENT_MEDIA_READY_V3' }, [channel.port2]);
  });
  return controller;
}
export async function createTmsMediaBridge(read, check) {
  check(); ready ??= enable().catch(error => { ready = null; throw error; }); const controller = await bounded(ready); check();
  if (navigator.serviceWorker.controller !== controller || controller.state !== 'activated') {
    ready = null; throw new Error('The media worker changed. Reopen this lesson.');
  }
  const token = crypto.randomUUID(), entry = { read, check, requests: new Set() }; entries.set(token, entry);
  return { url: `/__content_media_v3/${token}`, release() { entries.delete(token); entry.requests.forEach(request => request.abort()); entry.requests.clear(); } };
}
